import { EkranBledu, EkranStartowy, TRESCI_EKRANU_BLEDU } from '@insolitum/ui';
import React from 'react';
import { Link } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import { getModuleDef } from '../../modules/registry';
import { useAuth } from '../../providers/AuthProvider';
import {
  ModuleAccessGuard,
  ModuleDisabledFallback,
  ModuleFrame,
  ModuleLoadingFallback,
  nazwaOrganuWZdaniu,
} from '../../utils/federation';

import { komunikatPuliEtykiet, type PulaEtykietUcs } from './ucs/pulaEtykiet';

// =============================================================================
// UCS MODULE PAGE — native page for the UCS shipping module. Unlike generated
// iframe routes, the UCS panel needs a one-time SSO ticket minted by
// neurosense-core (POST /api/ucs/sso-ticket) BEFORE the iframe is mounted:
// the embedded UCS web app exchanges the ticket for its own session cookies.
// A fresh ticket is fetched on every mount (tickets are single-use); once the
// UCS session exists, subsequent visits short-circuit inside the iframe.
// =============================================================================

const UCS_SLUG = 'ucs';
const UCS_NAME = 'UCS Shipping';

/** Auto-retry cadence while neurosense-core reports 503 ucs_provisioning. */
const PROVISIONING_RETRY_MS = 5_000;
const PROVISIONING_MAX_ATTEMPTS = 6;

// ─── API helper (copy of the file-private apiFetch pattern in useBilling.ts,
//     extended with a typed error so the page can detect 503 ucs_provisioning) ─

const API_BASE = import.meta.env.VITE_NEUROSENSE_API_URL || 'https://api.insolitum.ai';

class UcsApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'UcsApiError';
  }
}

async function apiFetch<T>(
  path: string,
  organizationId: string,
  options?: RequestInit
): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;

  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-Tenant-Id': organizationId,
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new UcsApiError(
      body.message || body.error || `API error: ${response.status}`,
      response.status,
      typeof body.error === 'string' ? body.error : undefined
    );
  }

  return response.json() as Promise<T>;
}

// ─── UI states ───────────────────────────────────────────────────────────────
//
// Każdy stan tej strony jest ekranem znaku, a nie własną kartą — tą samą
// rodziną, co ramka organu, która nie wstała, i organ wyłączony dla
// organizacji (`utils/federation.tsx`). Obraz wynika z tego, co naprawdę
// wiadomo (symbolika: docs/marka/ZNAK-W-SYSTEMIE.md §1):
//
// | sytuacja                                         | obraz                               |
// | ------------------------------------------------ | ----------------------------------- |
// | konto UCS dla organizacji jeszcze powstaje (503) | start: `EkranStartowy` (praca)      |
// | …i nie powstało po ostatniej próbie              | ten sam start (oddech) + ponowienie |
// | …albo odpytywanie dostało 5xx / 4xx / nic        | ten sam start (oddech) + awaria     |
// | 5xx albo brak odpowiedzi na WEJŚCIU              | `organ-niedostepny`                 |
// | 4xx, brak sesji, wszystko inne na WEJŚCIU        | `blad`                              |
// | brak organizacji                                 | strażnik organu                     |
//
// Awaria W TRAKCIE aktywacji (runda 7c) zostaje na ekranie startu, który
// już stoi: ten sam węzeł znaku w `oddech`, w miejscu treści zdanie awarii
// (te same słowa co `EkranBledu` dla tej przyczyny), ponowienie, pulpit
// i szczegóły techniczne. Do rundy 7c 502 w piątej sekundzie odpytywania
// przenosiło znak z punktu startu na kotwicę ekranu błędu (Δ 154 px na
// desktopie) i zamieniało znak pionowy „INSOLITUM / UCS SHIPPING” w sam
// sygnet — skok znaku w środku jednego przepływu (§3a). `EkranBledu` dostaje
// wyłącznie błąd na wejściu, zanim pokazał się start.
//
// Aktywacja to normalny stan w toku, nie awaria: do rundy 7 stała na
// `organ-niedostepny` (uśpiony organ, „Organ UCS Shipping”) z jednym
// przyciskiem drugorzędnym, czyli wyglądała jak ekran awarii. Teraz to start
// organu — znak pracuje w punkcie startu, pod nim nazwa organu.
//
// Zdanie serwera (po angielsku: „Failed to create UCS SSO ticket", „Access
// denied to this organization") nie trafia do treści — kod odpowiedzi
// i ścieżka zapytania jadą w szczegółach technicznych, a zdanie dla człowieka
// jest po polsku. Trasa `/admin/modules/ucs` nie ma nad sobą paska powłoki,
// więc każdy ekran awarii ma obok ponowienia wyjście na pulpit.

/** Ścieżka zapytania o bilet — do szczegółów technicznych ekranu awarii. */
const SCIEZKA_BILETU = '/api/ucs/sso-ticket';

/** Nazwa organu w zdaniu („UCS Shipping") — z tej samej mapy co bramka organu. */
const NAZWA = nazwaOrganuWZdaniu(UCS_SLUG, UCS_NAME);

/** Dlaczego biletu nie ma — od tego zależy, który ekran mówi prawdę. */
type PrzyczynaBledu = 'aktywacja' | 'organ' | 'blad';

/** Tytuł ekranu awarii „pytanie poszło źle” — ten sam na wejściu i w trakcie aktywacji. */
const TYTUL_BLEDU = `Nie udało się otworzyć modułu ${NAZWA}`;

/**
 * Zdania awarii według przyczyny — TE SAME, co na ekranie awarii na wejściu
 * (`EkranBledu`: tytuł wariantu z nazwą organu i jego treść domyślna), żeby
 * awaria w trakcie aktywacji nie gubiła informacji tylko dlatego, że stoi
 * na ekranie startu. Ekran startu nie ma tytułu, więc tytuł otwiera treść
 * i jest komunikatem dla czytnika.
 */
const ZDANIA_AWARII: Readonly<
  Record<Exclude<PrzyczynaBledu, 'aktywacja'>, { tytul: string; tresc: string }>
> = {
  organ: {
    tytul: `${NAZWA} chwilowo nie odpowiada`,
    tresc: TRESCI_EKRANU_BLEDU['organ-niedostepny'].tresc,
  },
  blad: { tytul: TYTUL_BLEDU, tresc: TRESCI_EKRANU_BLEDU.blad.tresc },
};

type PageState =
  | { phase: 'loading' }
  | { phase: 'provisioning' }
  | {
      phase: 'error';
      przyczyna: PrzyczynaBledu;
      kod?: string;
      /** Ekran startu już stał (odpytywanie aktywacji) — awaria zostaje na nim. */
      wTrakcieAktywacji?: boolean;
    }
  | { phase: 'ready'; ticket: string; pula?: PulaEtykietUcs | null };

/**
 * Przyczyna nieudanego zapytania o bilet. 5xx i brak odpowiedzi (`fetch`
 * odrzuca obietnicę `TypeError`-em, gdy odpowiedź nie przyszła wcale) to
 * organ, który nie oddał biletu; 4xx i reszta to pytanie, które poszło źle.
 */
function przyczynaBledu(err: unknown): { przyczyna: PrzyczynaBledu; kod?: string } {
  if (err instanceof UcsApiError) {
    return { przyczyna: err.status >= 500 ? 'organ' : 'blad', kod: String(err.status) };
  }
  if (err instanceof TypeError) return { przyczyna: 'organ' };
  if (err instanceof Error && err.message === 'Not authenticated') {
    return { przyczyna: 'blad', kod: 'BRAK_SESJI' };
  }
  return { przyczyna: 'blad' };
}

/**
 * Konto organizacji w UCS powstaje — to START organu, nie awaria
 * (§1: „start, logowanie, aktywacja”): `EkranStartowy` z nazwą organu pod
 * znakiem, znak w punkcie startu, sieć pracuje. Strona pyta sama, co
 * `PROVISIONING_RETRY_MS`.
 *
 * Pod znakiem zdanie, co się dzieje, i wyjście — w miejscu ekranu startu na
 * treść i akcje (`tresc`, `akcje`): w przepływie, rytm z arkusza znaku
 * (24 px pod wierszem „To trwa dłużej…”, akcje 32 px niżej), a znak zostaje
 * w punkcie startu także wtedy, gdy niskie okno (telefon poziomo) przewija
 * stronę. Czekanie do `PROVISIONING_MAX_ATTEMPTS` prób na trasie bez paska
 * powłoki nie może być ślepą uliczką. Ponowienia w czasie odpytywania nie ma
 * (strona robi to sama), więc jedyna akcja jest drugorzędna.
 *
 * Po ostatniej próbie (`poLimicie`) nic już samo nie pyta — system czeka na
 * człowieka, więc znak przechodzi z pracy w oddech (§1: „system czeka na
 * człowieka → oddech”). To ten sam ekran i ten sam węzeł znaku (start nie
 * zaczyna się od nowa, „To trwa dłużej niż zwykle.” stoi już pod nazwą);
 * zmienia się zdanie, dochodzi „Spróbuj ponownie” jako akcja główna,
 * a komunikat dla czytnika w regionie `role="status"` ekranu startu ogłasza
 * zmianę.
 *
 * Awaria w trakcie odpytywania (`blad` — 5xx, brak odpowiedzi, 4xx) to ten
 * sam ekran i ten sam węzeł znaku w oddechu: treść to tytuł i zdanie ekranu
 * awarii tej przyczyny (`ZDANIA_AWARII`), komunikat dla czytnika — tytuł,
 * akcje jak po limicie, pod nimi szczegóły techniczne (kod, ścieżka). Bez
 * wiersza „To trwa dłużej…” (`trwa={false}`): nic już nie trwa.
 *
 * Zdania nie są wierszem stanu ani regionem `aria-live` — nie zmieniają się
 * w trakcie odpytywania, więc nie ma czego ogłaszać poza tym, co ogłasza
 * ekran startu. Bez `aria-busy` na ekranie: obejmuje on teraz treść
 * i akcje, a czytnik może wstrzymać pokazywanie zawartości „zajętego”
 * elementu — razem z wyjściem na pulpit. Kontrast tekstu stonowanego na
 * poświacie w trybie jasnym załatwia arkusz znaku
 * (`.ids-ekran-startowy--poswiata`), nie ta strona.
 */
const EkranAktywacji = ({
  poLimicie = false,
  blad,
  onPonow,
}: {
  poLimicie?: boolean;
  /** Awaria w trakcie odpytywania — ten sam ekran, zdanie awarii zamiast czekania. */
  blad?: { przyczyna: Exclude<PrzyczynaBledu, 'aktywacja'>; kod?: string };
  onPonow?: () => void;
}) => {
  const czeka = poLimicie || Boolean(blad);
  const awaria = blad ? ZDANIA_AWARII[blad.przyczyna] : null;
  return (
    <EkranStartowy
      uklad="pelny"
      poswiata
      tryb={czeka ? 'oddech' : 'praca'}
      trwa={!awaria}
      nazwa={NAZWA}
      komunikat={
        awaria
          ? awaria.tytul
          : poLimicie
            ? `Aktywacja ${NAZWA} trwa dłużej niż zwykle`
            : `Aktywujemy ${NAZWA}…`
      }
      tresc={
        <p>
          {awaria
            ? `${awaria.tytul}. ${awaria.tresc}`
            : poLimicie
              ? `Sprawdziliśmy ${PROVISIONING_MAX_ATTEMPTS} razy, a ${NAZWA} jeszcze się przygotowuje. Spróbuj ponownie za chwilę.`
              : `Przygotowujemy moduł dla Twojej organizacji — to może potrwać chwilę. Sprawdzamy co ${PROVISIONING_RETRY_MS / 1000}\u00A0s, czy moduł jest gotowy.`}
        </p>
      }
      akcje={
        <>
          {czeka && onPonow ? (
            <button type="button" className="btn btn--primary btn--lg" onClick={onPonow}>
              Spróbuj ponownie
            </button>
          ) : null}
          <Link to="/admin" className="btn btn--secondary btn--lg">
            Przejdź do pulpitu
          </Link>
        </>
      }
      szczegoly={blad ? { kod: blad.kod, sciezka: SCIEZKA_BILETU } : undefined}
    />
  );
};

/**
 * Biletu nie ma, bo organ nie oddał go albo pytanie poszło źle — ekran awarii
 * według przyczyny, zawsze z ponowieniem i wyjściem. Wyłącznie błąd na
 * WEJŚCIU (pierwsze pytanie, zanim pokazał się start); w trakcie aktywacji
 * ta sama przyczyna to `EkranAktywacji blad`. (Przedłużona aktywacja to nie
 * awaria: `EkranAktywacji poLimicie`.)
 */
const EkranBleduUcs = ({
  przyczyna,
  kod,
  onPonow,
}: {
  przyczyna: Exclude<PrzyczynaBledu, 'aktywacja'>;
  kod?: string;
  onPonow: () => void;
}) => {
  const akcje = (
    <>
      <button type="button" className="btn btn--primary btn--lg" onClick={onPonow}>
        Spróbuj ponownie
      </button>
      <Link to="/admin" className="btn btn--secondary btn--lg">
        Przejdź do pulpitu
      </Link>
    </>
  );
  const szczegoly = { kod, sciezka: SCIEZKA_BILETU };

  switch (przyczyna) {
    case 'organ':
      return (
        <EkranBledu
          wariant="organ-niedostepny"
          uklad="pelny"
          organ={NAZWA}
          akcje={akcje}
          szczegoly={szczegoly}
        />
      );
    case 'blad':
      return (
        <EkranBledu
          wariant="blad"
          uklad="pelny"
          tytul={TYTUL_BLEDU}
          akcje={akcje}
          szczegoly={szczegoly}
        />
      );
  }
};

// ─── Page ────────────────────────────────────────────────────────────────────

export function UcsModulePage() {
  const { currentOrganization, isLoading: authLoading } = useAuth();
  const organizationId = currentOrganization?.organization_id ?? null;
  const [state, setState] = React.useState<PageState>({ phase: 'loading' });
  // Czy w tej organizacji stał już ekran startu (pierwsze 503 `ucs_provisioning`).
  // „Spróbuj ponownie” z tego ekranu nie wraca przez szkielet, a awaria
  // pierwszej próby PO ponowieniu zostaje na starcie — inaczej znak skakałby
  // na kotwicę `EkranBledu` jedno kliknięcie później (kontrola rundy 7c:
  // 18 klatek szkieletu przy 1280 × 800, potem rdzeń na y 246 zamiast 400).
  const startStoi = React.useRef(false);
  React.useEffect(() => {
    startStoi.current = false;
  }, [organizationId]);
  // Bumped by the manual retry button to restart the ticket fetch.
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    if (!organizationId) return;

    let cancelled = false;
    let timer: number | undefined;

    const fetchTicket = async (attempt: number) => {
      try {
        const { ticket, pula } = await apiFetch<{ ticket: string; pula?: PulaEtykietUcs | null }>(
          '/api/ucs/sso-ticket',
          organizationId,
          // Send an explicit empty JSON object: apiFetch always sets
          // `Content-Type: application/json`, and Fastify rejects a POST that
          // declares that content-type with an empty body ("Body cannot be
          // empty…"). The endpoint derives everything from the auth headers,
          // so an empty object is the correct payload.
          { method: 'POST', body: '{}' }
        );
        if (!cancelled) {
          setState({ phase: 'ready', ticket, pula: pula ?? null });
        }
      } catch (err) {
        if (cancelled) return;

        // Tenant not provisioned in UCS yet — poll until ready (max ~30 s).
        if (err instanceof UcsApiError && err.status === 503 && err.code === 'ucs_provisioning') {
          startStoi.current = true;
          if (attempt < PROVISIONING_MAX_ATTEMPTS) {
            setState({ phase: 'provisioning' });
            timer = window.setTimeout(() => fetchTicket(attempt + 1), PROVISIONING_RETRY_MS);
          } else {
            setState({ phase: 'error', przyczyna: 'aktywacja', kod: err.code });
          }
          return;
        }

        console.error('[UcsModulePage] Failed to fetch SSO ticket:', err);
        // Ekran aktywacji już stoi, gdy to kolejna próba odpytywania albo
        // pierwsza próba po ponowieniu z tego ekranu — awaria zostaje na nim.
        // Sam ref wystarcza: próbę > 1 planuje wyłącznie gałąź 503 wyżej,
        // zaraz po ustawieniu `startStoi`.
        setState({
          phase: 'error',
          ...przyczynaBledu(err),
          wTrakcieAktywacji: startStoi.current,
        });
      }
    };

    // Fresh single-use ticket on every mount — never cached. Ponowienie
    // z ekranu startu zostaje na nim (ten sam węzeł znaku), nie na szkielecie.
    setState(startStoi.current ? { phase: 'provisioning' } : { phase: 'loading' });
    fetchTicket(1);

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [organizationId, reloadKey]);

  const def = getModuleDef(UCS_SLUG);

  if (authLoading) {
    return <ModuleLoadingFallback name={UCS_NAME} />;
  }

  if (!organizationId) {
    // Bez organizacji strażnik organu sam wybiera jedną z trzech prawd —
    // lista się wczytuje (szkielet), lista nie przyszła („Brak odpowiedzi"
    // z ponowieniem) albo konto nie ma czynnej organizacji („Nie wiemy, o którą
    // organizację pytać") — tymi samymi ekranami co przy każdym organie.
    // Do treści nie dochodzi: bez organizacji nie ma kogo pytać o dostęp.
    return (
      <ModuleAccessGuard moduleId={UCS_SLUG} moduleName={UCS_NAME}>
        {null}
      </ModuleAccessGuard>
    );
  }

  if (!def?.url) {
    return <ModuleDisabledFallback name={def?.displayName ?? UCS_NAME} moduleId={UCS_SLUG} />;
  }

  switch (state.phase) {
    case 'loading':
      return <ModuleLoadingFallback name={UCS_NAME} />;
    case 'provisioning':
      return <EkranAktywacji />;
    case 'error':
      // Przedłużona aktywacja i awaria w jej trakcie zostają TYM SAMYM
      // ekranem startu co odpytywanie (ten sam komponent w tym samym miejscu
      // drzewa, więc znak nie przemontowuje się i nie skacze). Nic już samo
      // nie pyta, więc znak przechodzi z pracy w oddech; dochodzi ponowienie.
      if (state.przyczyna === 'aktywacja') {
        return <EkranAktywacji poLimicie onPonow={() => setReloadKey((k) => k + 1)} />;
      }
      if (state.wTrakcieAktywacji) {
        return (
          <EkranAktywacji
            blad={{ przyczyna: state.przyczyna, kod: state.kod }}
            onPonow={() => setReloadKey((k) => k + 1)}
          />
        );
      }
      return (
        <EkranBleduUcs
          przyczyna={state.przyczyna}
          kod={state.kod}
          onPonow={() => setReloadKey((k) => k + 1)}
        />
      );
    case 'ready': {
      // Pula etykiet (plan 56 etap 4): pasek tylko przy twardej puli na
      // wyczerpaniu — inaczej nic nie dokłada się nad ramką.
      const komunikat = komunikatPuliEtykiet(state.pula);
      return (
        <ModuleAccessGuard moduleId={UCS_SLUG} moduleName={UCS_NAME}>
          {komunikat && (
            <p
              role="status"
              data-testid="ucs-pula-etykiet"
              className="px-4 py-2 text-sm text-aurora-muted"
            >
              {komunikat}
            </p>
          )}
          <ModuleFrame
            baseUrl={def.url}
            path={'/embedded#ticket=' + encodeURIComponent(state.ticket)}
            name={UCS_NAME}
          />
        </ModuleAccessGuard>
      );
    }
  }
}
